import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import MainSidebar from "components/MainSidebar";
import StockTable from "./components/StockTable";
import FilterBar from "./components/FilterBar";
import OrderBook from "./components/OrderBook";
import OrderPanel from "./components/OrderPanel";
import MiniChart from "./components/MiniChart";
import PriceAlertModal from "./components/PriceAlertModal";
import MarketStats from "./components/MarketStats";
import { supabase } from "../../lib/supabase";
import { useRealtimeSubscription } from "../../hooks/useRealtimeSubscription";
import { useRealtimeStockPrices } from '../../hooks/useRealtimeStockPrices';

import { queueTrade } from "../../lib/indexedDBQueue";
import { recordFetch } from "../../lib/dataFreshnessTracker";
import { useToast, TOAST_TYPE, TOAST_PRIORITY } from "../../hooks/useToast";
import { EmptyState } from 'components/SkeletonLoader';
import { useAuth } from '../../contexts/AuthContext';
import { logError } from '../../utils/logError';


const getSectorColor = (sector) => {
  const colors = {
    Tech: '#00D9FF', Retail: '#FFB800', Manufacturing: '#94A3B8',
    Financial: '#10B981', BlackMarket: '#EF4444', 'Black Market': '#EF4444',
    Energy: '#F59E0B', Pharma: '#8B5CF6', Defense: '#6366F1',
    Media: '#EC4899', Transport: '#14B8A6',
  };
  return colors?.[sector] || '#64748B';
};

const NOTIFICATION_COUNTS = { dashboard: 0, "stock-exchange": 3, portfolio: 1, notifications: 7 };
const USER_STATS = { username: "TraderX", level: 14, energy: 68, maxEnergy: 100, xp: 8750, xpToNext: 10000 };

const formatTimeAgo = (date) => {
  if (!date) return 'Never';
  const secs = Math.floor((Date.now() - date?.getTime()) / 1000);
  if (secs < 5) return 'Just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
};

const StockExchange = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("All");
  const [filter, setFilter] = useState("all");
  const [orderPanelOpen, setOrderPanelOpen] = useState(false);
  const [orderSide, setOrderSide] = useState("buy");
  const [alertModalStock, setAlertModalStock] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [lastUpdateAgo, setLastUpdateAgo] = useState('Just now');
  const [tickCountdown, setTickCountdown] = useState(60);
  const [rightPanel, setRightPanel] = useState("order");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState('idle');
  const [marketLive, setMarketLive] = useState(false);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [playerCash, setPlayerCash] = useState(0);
  const pollingRef = useRef(null);
  const realtimeFailedRef = useRef(false);
  const [stocksFromCache, setStocksFromCache] = useState(false);

  // ── Realtime stock price hook ──────────────────────────────
  const handleStockUpdate = useCallback((payload) => {
    const s = payload?.new;
    if (!s) return;
    setStocks(prev => prev?.map(stock =>
      stock?.id === s?.id ? {
        ...stock,
        price: parseFloat(s?.current_price || stock?.price),
        change: s?.open_price > 0
          ? parseFloat(((s?.current_price - s?.open_price) / s?.open_price * 100)?.toFixed(2))
          : stock?.change,
        volume: parseInt(s?.volume || stock?.volume),
        high: parseFloat(s?.high_price || stock?.high),
        low: parseFloat(s?.low_price || stock?.low),
        marketCap: parseFloat(s?.market_cap || stock?.marketCap),
      } : stock
    ));
    // Check price alerts
    setPriceAlerts(alerts => {
      alerts?.forEach(alert => {
        if (alert?.ticker === s?.ticker && alert?.is_active) {
          const newPrice = parseFloat(s?.current_price || 0);
          if ((alert?.alert_type === 'above' && newPrice >= alert?.target_price) ||
              (alert?.alert_type === 'below' && newPrice <= alert?.target_price)) {
            showToast(
              `📈 ${s?.ticker} reached target price $${parseFloat(alert?.target_price)?.toFixed(2)}`,
              TOAST_TYPE?.PRICE_ALERT,
              TOAST_PRIORITY?.HIGH
            );
          }
        }
      });
      return alerts;
    });
  }, [showToast]);

  const {
    subscriptionStatus: rtStatus,
    isLive,
    lastUpdatedLabel,
    subscribe: subscribeStocks,
    forceReconnect: forceStockReconnect,
  } = useRealtimeStockPrices(handleStockUpdate, { enabled: true });

  // Add missing function declarations
  const invalidateStockPrices = useCallback(() => {
    // Invalidate cache logic - placeholder implementation
    console.log('Invalidating stock price cache');
  }, []);

  const getStockPrices = useCallback(async () => {
    try {
      const { data, error } = await supabase
        ?.from('stocks')
        ?.select('*')
        ?.order('ticker', { ascending: true });
      
      if (error) throw error;
      
      return { data, fromCache: false, error: null };
    } catch (err) {
      return { data: null, fromCache: false, error: err?.message };
    }
  }, []);

  const fetchStocks = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
      invalidateStockPrices();
    } else if (!isManualRefresh && stocks?.length === 0) setLoading(true);
    setFetchError(null);
    try {
      const { data: rawData, fromCache, error: cacheErr } = await getStockPrices();
      if (cacheErr) throw new Error(cacheErr);

      const mapped = (rawData || [])?.map(s => ({
        id: s?.id,
        ticker: s?.ticker,
        name: s?.company_name,
        price: parseFloat(s?.current_price || 0),
        change: s?.open_price > 0 ? parseFloat(((s?.current_price - s?.open_price) / s?.open_price * 100)?.toFixed(2)) : 0,
        volume: parseInt(s?.volume || 0),
        marketCap: parseFloat(s?.market_cap || 0),
        sector: s?.sector,
        high: parseFloat(s?.high_price || 0),
        low: parseFloat(s?.low_price || 0),
        dividendYield: parseFloat(s?.dividend_yield || 0),
        color: getSectorColor(s?.sector),
        sectorColor: getSectorColor(s?.sector),
      }));

      setStocks(mapped);
      setStocksFromCache(!!fromCache);
      setLastUpdate(new Date());
      recordFetch('stock_prices');

      if (user?.id) {
        const { data: statsData } = await supabase?.from('player_stats')?.select('cash')?.eq('user_id', user?.id)?.single();
        if (statsData) setPlayerCash(parseFloat(statsData?.cash || 0));
      }

      if (isManualRefresh) showToast('Stock prices refreshed', TOAST_TYPE?.SUCCESS, TOAST_PRIORITY?.LOW);
    } catch (err) {
      console.error('Stock fetch error:', err);
      setFetchError(err?.message || 'Failed to load stocks');
      logError({ error: err, componentName: 'StockExchange', actionName: 'fetchStocks', screenName: 'stock-exchange', userId: user?.id });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showToast, user?.id, invalidateStockPrices, getStockPrices, stocks?.length]);

  useEffect(() => { fetchStocks(); }, [fetchStocks]);

  // Start realtime subscription after initial fetch
  useEffect(() => {
    subscribeStocks(fetchStocks);
  }, [subscribeStocks, fetchStocks]);

  // Update "X ago" label every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdateAgo(formatTimeAgo(lastUpdate));
    }, 5000);
    setLastUpdateAgo(formatTimeAgo(lastUpdate));
    return () => clearInterval(interval);
  }, [lastUpdate]);

  // Realtime subscription for stock price updates
  const { status: _rtStatusLegacy } = useRealtimeSubscription(
    'stock-prices-broadcast',
    { table: 'stocks', event: 'UPDATE' },
    useCallback((payload) => {
      // Handled by useRealtimeStockPrices hook above
    }, []),
    { enabled: false } // Disabled — handled by useRealtimeStockPrices
  );

  useEffect(() => {
    setRealtimeStatus(rtStatus);
    if (rtStatus === 'connected') {
      setMarketLive(true);
      realtimeFailedRef.current = false;
      // Clear polling fallback if realtime reconnects
      if (pollingRef?.current) {
        clearInterval(pollingRef?.current);
        pollingRef.current = null;
      }
    } else if (rtStatus === 'error' || rtStatus === 'closed') {
      setMarketLive(false);
      realtimeFailedRef.current = true;
      // Fallback: poll every 30 seconds
      if (!pollingRef?.current) {
        pollingRef.current = setInterval(() => fetchStocks(), 30000);
      }
    } else if (rtStatus === 'connecting') {
      setMarketLive(false);
    }
  }, [rtStatus, fetchStocks]);

  useEffect(() => {
    return () => {
      if (pollingRef?.current) clearInterval(pollingRef?.current);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTickCountdown(t => t <= 1 ? 60 : t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleQuickOrder = (stock, side) => {
    setSelectedStock(stock);
    setOrderSide(side);
    setOrderPanelOpen(true);
    setRightPanel("order");
  };

  const handleOrderSubmit = async (order) => {
    if (!navigator.onLine) {
      await queueTrade({
        stock_id: order?.stock?.id,
        quantity: order?.quantity,
        order_type: order?.side,
        price: order?.price,
      });
      showToast('Offline — trade queued for retry when connection restores', TOAST_TYPE?.WARNING, TOAST_PRIORITY?.HIGH);
      return;
    }

    // Optimistic UI: add pending order immediately
    const pendingId = `pending-${Date.now()}`;
    const pendingOrder = {
      id: pendingId,
      ticker: order?.stock?.ticker,
      side: order?.side,
      quantity: order?.quantity,
      price: order?.price,
      status: 'pending',
    };
    setPendingOrders(prev => [...prev, pendingOrder]);

    // Optimistically deduct cash for buy orders
    const prevCash = playerCash;
    if (order?.side === 'buy') {
      setPlayerCash(prev => prev - (order?.price * order?.quantity * 1.001));
    }

    try {
      const { data, error } = await supabase?.rpc('place_market_order', {
        p_stock_id: order?.stock?.id,
        p_order_type: order?.side,
        p_quantity: order?.quantity,
        p_price: order?.price,
      });

      if (error) throw error;
      if (data && !data?.success) throw new Error(data?.error || 'Order failed');

      // Confirm: remove pending badge
      setPendingOrders(prev => prev?.filter(o => o?.id !== pendingId));

      showToast(
        `✓ Trade executed: ${order?.side === 'buy' ? 'Bought' : 'Sold'} ${order?.quantity} ${order?.stock?.ticker} @ $${order?.price?.toFixed(2)}`,
        TOAST_TYPE?.TRADE_SUCCESS,
        TOAST_PRIORITY?.NORMAL
      );

      // Refresh cash after confirmed trade
      if (user?.id) {
        const { data: statsData } = await supabase?.from('player_stats')?.select('cash')?.eq('user_id', user?.id)?.single();
        if (statsData) setPlayerCash(parseFloat(statsData?.cash || 0));
      }
    } catch (err) {
      // Rollback: remove pending order, restore cash
      setPendingOrders(prev => prev?.filter(o => o?.id !== pendingId));
      setPlayerCash(prevCash);

      const reason = err?.message || 'Unknown error';
      const isInsufficientFunds = reason?.toLowerCase()?.includes('insufficient') || reason?.toLowerCase()?.includes('funds');
      showToast(
        `✗ Trade failed: ${isInsufficientFunds ? 'Insufficient funds' : reason}`,
        TOAST_TYPE?.TRADE_FAILED,
        TOAST_PRIORITY?.HIGH
      );
      logError({ error: err, componentName: 'StockExchange', actionName: 'handleOrderSubmit', screenName: 'stock-exchange', userId: user?.id, context: { order } });
    }
  };

  const filteredStocks = stocks?.filter(s => {
    const matchSearch = s?.ticker?.toLowerCase()?.includes(search?.toLowerCase()) || s?.name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchSector = sector === "All" || s?.sector === sector;
    let matchFilter = true;
    if (filter === "gainers") matchFilter = s?.change > 0;
    else if (filter === "losers") matchFilter = s?.change < 0;
    else if (filter === "volume") matchFilter = s?.volume > 40000;
    return matchSearch && matchSector && matchFilter;
  });

  return (
    <div className="min-h-screen bg-background flex">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        userRole="player"
        notificationCounts={NOTIFICATION_COUNTS}
        userStats={USER_STATS}
      />
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-250 pb-16 lg:pb-0 ${sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-[280px]"}`}>

        <header className="sticky top-0 z-40 bg-secondary/95 backdrop-blur border-b border-border px-4 md:px-6 py-3 flex items-center gap-3">
          <div className="lg:hidden w-10" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Icon name="BarChart2" size={20} color="var(--color-primary)" />
            <h1 className="font-heading text-base md:text-lg font-bold text-foreground truncate">Stock Exchange</h1>
            <span className="hidden md:flex items-center gap-1 font-caption text-xs text-muted-foreground bg-secondary border border-border px-2 py-1 rounded-full">
              <span className={`w-1.5 h-1.5 rounded-full ${tickCountdown > 10 ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} />
              Tick in {tickCountdown}s
            </span>
            {/* Market Live Indicator */}
            <span className={`hidden md:flex items-center gap-1.5 font-caption text-xs px-2 py-1 rounded-full border transition-all ${
              marketLive
                ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
                : realtimeFailedRef?.current
                  ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' :'text-muted-foreground bg-secondary border-border'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                marketLive ? 'bg-emerald-400 animate-pulse' : realtimeFailedRef?.current ? 'bg-amber-400' : 'bg-zinc-500'
              }`} />
              {marketLive ? 'Market Live' : realtimeFailedRef?.current ? 'Polling 30s' : 'Connecting...'}
            </span>
          </div>

          <div className="hidden md:flex items-center gap-2">
            {[
              { label: "Dashboard", path: "/dashboard", icon: "LayoutDashboard" },
              { label: "Portfolio", path: "/portfolio", icon: "PieChart" },
              { label: "Detail", path: "/stock-detail", icon: "TrendingUp" },
            ]?.map(nav => (
              <button key={nav?.path} onClick={() => navigate(nav?.path)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 border border-transparent hover:border-primary/20 transition-all min-h-[36px]">
                <Icon name={nav?.icon} size={13} color="currentColor" />
                {nav?.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="hidden lg:flex items-center gap-1 font-caption text-xs text-muted-foreground">
              <Icon name="Clock" size={11} color="currentColor" />
              Updated {lastUpdateAgo}
            </span>
            <button
              onClick={() => fetchStocks(true)}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 border border-border transition-all min-h-[36px] disabled:opacity-50"
              title="Refresh stock prices"
            >
              <Icon name={refreshing ? 'Loader2' : 'RefreshCw'} size={13} color="currentColor" className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            <Button variant="outline" size="sm" iconName="Bell" iconPosition="left" onClick={() => setAlertModalStock(selectedStock || stocks?.[0])}>
              <span className="hidden sm:inline">Alert</span>
            </Button>
          </div>
        </header>

        <MarketStats stocks={stocks} />

        {fetchError && (
          <div className="mx-4 mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={14} color="#f87171" />
              <span className="text-sm text-red-400">{fetchError}</span>
            </div>
            <button onClick={() => fetchStocks()} className="text-xs text-red-400 hover:text-red-300 underline">Retry</button>
          </div>
        )}

        <div className="flex-1 flex flex-col lg:flex-row gap-0 overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <FilterBar search={search} onSearch={setSearch} sector={sector} onSector={setSector} filter={filter} onFilter={setFilter} />

            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="p-4 space-y-2">
                  {Array(20)?.fill(0)?.map((_, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 rounded-lg border border-border bg-card animate-pulse" aria-busy="true" aria-label="Loading stocks">
                      <div className="w-12 h-8 rounded bg-white/5" />
                      <div className="flex-1 h-4 rounded bg-white/5" />
                      <div className="w-20 h-4 rounded bg-white/5" />
                      <div className="w-16 h-4 rounded bg-white/5" />
                    </div>
                  ))}
                </div>
              ) : filteredStocks?.length === 0 ? (
                <EmptyState
                  icon={<Icon name="SearchX" size={32} color="var(--color-muted-foreground)" />}
                  title="No stocks match your filters"
                  description={stocks?.length === 0 ? 'No stocks available' : 'Try adjusting your search criteria or clearing filters'}
                  actionLabel={stocks?.length > 0 ? "Clear Filters" : undefined}
                  action={stocks?.length > 0 ? () => { setSearch(''); setSector('All'); setFilter('all'); } : undefined}
                />
              ) : (
                <StockTable 
                  stocks={filteredStocks} 
                  onSelectStock={setSelectedStock} 
                  selectedStock={selectedStock} 
                  onQuickOrder={handleQuickOrder} 
                  pendingOrders={pendingOrders}
                  onReconnect={forceStockReconnect}
                />
              )}
            </div>

            <div className="px-4 py-2 border-t border-border flex items-center justify-between">
              <span className="font-caption text-xs text-muted-foreground">{filteredStocks?.length} stocks listed</span>
              <div className="flex items-center gap-3">
                <button onClick={() => navigate("/businesses")} className="font-caption text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 min-h-[36px]">
                  <Icon name="Building2" size={12} color="currentColor" />Businesses
                </button>
                <button onClick={() => navigate("/crime-hub")} className="font-caption text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 min-h-[36px]">
                  <Icon name="Skull" size={12} color="currentColor" />Crime Hub
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Desktop */}
          <div className="hidden lg:flex flex-col w-80 xl:w-96 border-l border-border bg-card flex-shrink-0">
            <div className="flex border-b border-border">
              {[
                { id: "order", label: "Order", icon: "ShoppingCart" },
                { id: "book", label: "Order Book", icon: "BookOpen" },
                { id: "chart", label: "Chart", icon: "TrendingUp" },
              ]?.map(tab => (
                <button key={tab?.id} onClick={() => setRightPanel(tab?.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 font-caption text-xs font-medium transition-all border-b-2 min-h-[48px] ${
                    rightPanel === tab?.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}>
                  <Icon name={tab?.icon} size={13} color="currentColor" />
                  {tab?.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto">
              {rightPanel === "order" && <OrderPanel stock={selectedStock} defaultSide={orderSide} onSubmit={handleOrderSubmit} onClose={() => setOrderPanelOpen(false)} playerCash={playerCash} />}
              {rightPanel === "book" && <OrderBook stock={selectedStock} />}
              {rightPanel === "chart" && <MiniChart stock={selectedStock} />}
            </div>
            {selectedStock && (
              <div className="p-3 border-t border-border">
                <Button variant="outline" size="sm" fullWidth iconName="Bell" iconPosition="left" onClick={() => setAlertModalStock(selectedStock)}>
                  Set Price Alert for {selectedStock?.ticker}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Order Panel */}
        {orderPanelOpen && (
          <div
            className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end"
            style={{ background: "rgba(0,0,0,0.85)" }}
            onClick={(e) => { if (e?.target === e?.currentTarget) setOrderPanelOpen(false); }}
          >
            <div className="bg-card border-t border-border rounded-t-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <span className="font-heading text-sm font-semibold text-foreground">Place Order</span>
                <button onClick={() => setOrderPanelOpen(false)} className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-primary/10 transition-all">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <OrderPanel stock={selectedStock} defaultSide={orderSide} onClose={() => setOrderPanelOpen(false)} onSubmit={handleOrderSubmit} playerCash={playerCash} />
            </div>
          </div>
        )}

        {selectedStock && !orderPanelOpen && (
          <div className="lg:hidden fixed bottom-16 left-0 right-0 z-30 px-4 py-3 bg-card/95 backdrop-blur border-t border-border flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm font-bold text-foreground">{selectedStock?.ticker}</div>
              <div className="font-data text-xs text-muted-foreground">${selectedStock?.price?.toFixed(2)}</div>
            </div>
            <Button variant="success" size="sm" onClick={() => { setOrderSide("buy"); setOrderPanelOpen(true); }}>Buy</Button>
            <Button variant="danger" size="sm" onClick={() => { setOrderSide("sell"); setOrderPanelOpen(true); }}>Sell</Button>
            <Button variant="outline" size="sm" iconName="Bell" onClick={() => setAlertModalStock(selectedStock)} />
          </div>
        )}
      </div>
      {alertModalStock && (
        <PriceAlertModal
          stock={alertModalStock}
          onClose={() => setAlertModalStock(null)}
          onSave={(alert) => {
            setPriceAlerts(prev => [...prev, { ...alert, is_active: true }]);
            showToast(
              `🔔 Alert set: ${alert?.stock?.ticker} ${alert?.alertType === 'above' ? 'above' : 'below'} $${parseFloat(alert?.targetPrice)?.toFixed(2)}`,
              TOAST_TYPE?.INFO,
              TOAST_PRIORITY?.LOW
            );
          }}
        />
      )}
    </div>
  );
};

export default StockExchange;